import { useParams } from 'react-router-dom';
import { User, USER_TYPE_ADMIN } from 'types/auth/types';
import { useCallback, useEffect, useState } from 'react';
import axiosService from '../../../utils/axios';
import LoadingSpinner from '../../../components/Shared/LoadingSpinner/LoadingSpinner';
import { Box, Card, CardContent, CardHeader, Typography } from '@mui/material';
import { userPageStyles } from './styles';
import CardTitle from '../../../components/Shared/CardsView/CardTitle';
import ModalErrorPopup from '../../../components/Modal/ModalErrorPopup';
import * as React from 'react';
import AddOrEditUser from '../list/AddOrEditUser';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import ActionButton from 'components/Shared/Buttons/ActionButton';
import PaginatedVehicleList from 'pages/vehicles/list/PaginatedVehicleList';

function UserPage() {
  const { id } = useParams();

  const loggedInUser = useSelector((state: RootState) => state.auth.user);

  const [user, setUser] = useState<User>();
  const [isLoadingUser, setIsLoadingUser] = useState(false);

  const [errorMessage, setErrorMessage] = useState('');
  const [editUserModalOpen, setEditUserModalOpen] = useState(false);

  const getUser = useCallback(() => {
    if (id) {
      setIsLoadingUser(true);
      axiosService
        .get(`/user/${id}`)
        .then(res => setUser(res.data))
        .catch(() => setErrorMessage('User not found.'))
        .finally(() => setIsLoadingUser(false));
    }
  }, [id]);

  useEffect(() => {
    getUser();
  }, [getUser]);

  const onUserEdit = () => {
    getUser();
    setEditUserModalOpen(false);
  };

  return (
    <>
      {isLoadingUser ? <LoadingSpinner /> : null}
      {user ? (
        <Box sx={userPageStyles.container}>
          <Box sx={userPageStyles.headerContainer}>
            <Typography color="text" sx={userPageStyles.headerTitle}>
              {`${user.first_name} ${user.last_name}`}
            </Typography>
            <Box sx={userPageStyles.actionsContainer}>
              {loggedInUser?.user_type === USER_TYPE_ADMIN ? (
                <ActionButton
                  type="edit"
                  onClick={e => {
                    e?.preventDefault();
                    e?.stopPropagation();
                    setEditUserModalOpen(true);
                  }}
                >
                  User
                </ActionButton>
              ) : null}
            </Box>
          </Box>
          <Box sx={userPageStyles.contentBox}>
            <Box sx={userPageStyles.row}>
              <Card elevation={1} sx={userPageStyles.card}>
                <CardHeader title={<CardTitle title="Details" tooltipText={id} />} sx={userPageStyles.cardHeader} />
                <CardContent sx={userPageStyles.cardContent}>
                  <Box sx={userPageStyles.detailsGrid}>
                    <Box sx={userPageStyles.detailBox}>
                      <Typography sx={userPageStyles.detailTitle}>First name</Typography>
                      <Typography sx={userPageStyles.detailValue}>{user.first_name || '-'}</Typography>
                    </Box>
                    <Box sx={userPageStyles.detailBox}>
                      <Typography sx={userPageStyles.detailTitle}>Last name</Typography>
                      <Typography sx={userPageStyles.detailValue}>{user.last_name || '-'}</Typography>
                    </Box>
                    <Box sx={userPageStyles.detailBox}>
                      <Typography sx={userPageStyles.detailTitle}>Email</Typography>
                      <Typography sx={userPageStyles.detailValue}>{user.email || '-'}</Typography>
                    </Box>
                    <Box sx={userPageStyles.detailBox}>
                      <Typography sx={userPageStyles.detailTitle}>Phone</Typography>
                      <Typography sx={userPageStyles.detailValue}>{user.phone || '-'}</Typography>
                    </Box>
                    <Box sx={userPageStyles.detailBox}>
                      <Typography sx={userPageStyles.detailTitle}>Address</Typography>
                      <Typography sx={userPageStyles.detailValue}>{user.address || '-'}</Typography>
                    </Box>
                    <Box sx={userPageStyles.detailBox}>
                      <Typography sx={userPageStyles.detailTitle}>Username</Typography>
                      <Typography sx={userPageStyles.detailValue}>{user.username || '-'}</Typography>
                    </Box>
                    <Box sx={userPageStyles.detailBox}>
                      <Typography sx={userPageStyles.detailTitle}>Active</Typography>
                      <Typography sx={userPageStyles.detailValue}>{user.is_active ? 'Yes' : 'No'}</Typography>
                    </Box>
                    <Box sx={userPageStyles.detailBox}>
                      <Typography sx={userPageStyles.detailTitle}>User type</Typography>
                      <Typography sx={userPageStyles.detailValue}>{user.user_type_display || '-'}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
            <Box sx={userPageStyles.vehiclesRow}>
              <Card elevation={1} sx={userPageStyles.vehiclesCard}>
                <CardHeader title={<CardTitle title="Vehicles" tooltipText={id} />} sx={userPageStyles.cardHeader} />
                <CardContent
                  id="userVehiclesContainer"
                  sx={{ ...userPageStyles.vehiclesCardContent, padding: { xs: '0', sm: '0 15px' } }}
                >
                  <PaginatedVehicleList clientId={id} shortRepresentation scrollableTarget="userVehiclesContainer" />
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      ) : null}
      {user ? (
        <AddOrEditUser
          user={user}
          onSave={onUserEdit}
          modalTitle="Edit user"
          isModalOpen={editUserModalOpen}
          setModalOpen={setEditUserModalOpen}
        />
      ) : null}
      {/* @ts-ignore:next-line*/}
      <ModalErrorPopup title="Error" message={errorMessage} onClose={() => setErrorMessage('')} />
    </>
  );
}

export default function UserPageRoute() {
  const { id } = useParams();
  return <UserPage key={id} />;
}
